import { GenericToolApprovalContent } from "#components/ui/tool-approval/GenericToolApprovalContent.tsx";
import type { ToolApprovalBatch, ToolApprovalSibling } from "#components/ui/tool-approval/renderers/ToolApprovalRenderer.types.ts";
import { DraftActions, DraftFooter, EditErrors } from "#features/conversations/components/prepared-action/cardControls.tsx";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import type { PendingApprovalCall, ToolApprovalEditError } from "#types/ToolApproval.ts";
import { Button, Text } from "@hopper-ui/components";
import { IconPencilBox } from "@intentui/icons";
import { useEffect, useId, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import "./ToolApprovalBatch.css";

interface Props {
    calls: PendingApprovalCall[];
    batch: ToolApprovalBatch;
    edits: Record<string, Record<string, unknown>>;
    onEditArgs: (toolCallId: string, partial: Record<string, unknown>) => void;
    onBookAll: () => void;
    onDiscard: () => void;
}

const NO_EDITS: Record<string, unknown> = {};
const NO_EDIT_ERRORS: ToolApprovalEditError[] = [];

// A re-presented rejection opens the first row whose edit it rejected.
function callToFix(calls: PendingApprovalCall[]): string | null {
    return calls.find(({ editErrors }) => editErrors && editErrors.length > 0)?.toolCallId ?? null;
}

interface RowProps extends Pick<Props, "batch" | "onEditArgs"> {
    call: PendingApprovalCall;
    edits: Record<string, unknown>;
    siblings: ToolApprovalSibling[];
    isOpen: boolean;
    onToggle: () => void;
}

function BatchRow({ call, batch, edits, siblings, isOpen, onToggle, onEditArgs }: RowProps) {
    const { tShared } = useSharedTranslation();
    const summaryId = useId();

    return (
        <>
            <div className="wai-ta-batch-line">
                <div id={summaryId} className="wai-ta-batch-summary">
                    <ErrorBoundary fallback={<GenericToolApprovalContent call={call} isBatch />}>
                        <batch.Row args={call.args} display={call.display ?? null} edits={edits} siblings={siblings} />
                    </ErrorBoundary>
                </div>
                <Button variant="ghost-secondary" size="xs" className="wai-pa-btn wai-pa-btn-plain" aria-describedby={summaryId} aria-expanded={isOpen} onPress={onToggle}>
                    <IconPencilBox aria-hidden="true" />
                    <Text>{tShared(isOpen ? "toolApproval.done" : "toolApproval.edit")}</Text>
                </Button>
            </div>
            {/* A folded row has no field to mark, so its rejected edit shows as the error itself. */}
            {!isOpen && <EditErrors errors={call.editErrors ?? NO_EDIT_ERRORS} />}
            {isOpen && (
                <ErrorBoundary fallback={<GenericToolApprovalContent call={call} isBatch />}>
                    <batch.Details
                        args={call.args}
                        display={call.display ?? null}
                        edits={edits}
                        onEditArgs={partial => onEditArgs(call.toolCallId, partial)}
                        editErrors={call.editErrors ?? NO_EDIT_ERRORS}
                        siblings={siblings}
                    />
                </ErrorBoundary>
            )}
        </>
    );
}

/**
 * A batch of drafted writes as a list: a compact row per call, one of which is open in place at a time, and one decision for
 * the whole batch. The edits live in the prompt, which posts once, on Book all or Discard.
 */
export function ToolApprovalBatchList({ calls, batch, edits, onEditArgs, onBookAll, onDiscard }: Props) {
    const { tShared } = useSharedTranslation();
    const [openId, setOpenId] = useState(() => callToFix(calls));
    useEffect(() => {
        setOpenId(callToFix(calls));
    }, [calls]);

    const toggle = (toolCallId: string) => setOpenId(current => (current === toolCallId ? null : toolCallId));

    return (
        <div className="wai-prepared-action wai-pa-approval">
            <div className="wai-pa-header">
                <batch.Glyph className="wai-pa-glyph" aria-hidden="true" />
                <Text size="xs" fontWeight="body-xs-medium" color="neutral-weak" className="wai-pa-label">
                    {tShared(batch.heading, { count: calls.length })}
                </Text>
            </div>
            <div className="wai-pa-content">
                <ul className="wai-ta-batch-rows">
                    {calls.map(call => (
                        <li key={call.toolCallId} className="wai-ta-batch-row">
                            <BatchRow
                                call={call}
                                batch={batch}
                                edits={edits[call.toolCallId] ?? NO_EDITS}
                                onEditArgs={onEditArgs}
                                siblings={calls.flatMap(({ toolCallId, args }, position) => (
                                    toolCallId === call.toolCallId ? [] : [{ position: position + 1, args, edits: edits[toolCallId] ?? NO_EDITS }]
                                ))}
                                isOpen={openId === call.toolCallId}
                                onToggle={() => toggle(call.toolCallId)}
                            />
                        </li>
                    ))}
                </ul>
            </div>
            <DraftFooter
                actions={(
                    <DraftActions
                        sendLabel={tShared(batch.bookAll, { count: calls.length })}
                        discardLabel={tShared("preparedAction.discard")}
                        onSend={onBookAll}
                        onDiscard={onDiscard}
                    />
                )}
            />
        </div>
    );
}
